import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';
import { useAuth } from '@/lib/auth/useAuth';
import type { CoCompanyPlanUsage } from '@/lib/api/types';

/**
 * Per-app, per-resource cap + counter returned by `/plans/current`.
 * Mirrors the backend `ResourceUsage` shape from the normalization PRD
 * (docs/prds/universal/plan-usage-normalization.md).
 */
export interface ResourceUsage {
  app: string;
  resource: string;
  limit: number;
  used: number;
  percent: number;
  reached: boolean;
}

/**
 * Effective-plan envelope. Legacy callers read flat `CoCompanyPlanUsage`
 * fields (maxBrands, maxUsers, …); new callers read `resources[]`.
 * The hook keeps both available so we can migrate gates resource-by-resource
 * without a big-bang switch.
 */
export type EffectivePlanUsage = CoCompanyPlanUsage & {
  resources?: ResourceUsage[];
  /** F2a: slug del plan BASE (xk1/xk2/free…) — elegibilidad de add-ons. */
  planSlug?: string | null;
  /** Nombre y precio anual del plan, para la cabecera de Facturación. */
  planName?: string | null;
  planValue?: number | null;
  daysRemaining?: number | null;
  /** F3: aniversario de la cuenta y su próxima renovación (co-terminación). */
  anniversaryDate?: string | null;
  renewsAt?: string | null;
};

export type QuotaResource =
  | 'projects'
  | 'events'
  | 'groups'
  | 'maxBrands'
  | 'maxUsers'
  | 'maxRoles'
  | 'maxSurveys'
  | 'xotskiEvents'
  | 'xotskiEventGroups'
  | 'xotskiStations'
  | 'xotskiStocks';

/**
 * Fetches the company's effective plan from the normalized `/plans/current`
 * endpoint. The response carries both the slim legacy `usage` block (used by
 * existing gates like `maxBrands`) and the new `resources[]` array for
 * per-app per-resource caps. The hook flattens `usage` to the top of the
 * returned object and attaches `resources` so legacy callers keep working
 * with the old field shape while new callers can read from `resources`.
 */
export function useCurrentPlan() {
  const { companyId } = useAuth();
  return useQuery<EffectivePlanUsage | null>({
    queryKey: queryKeys.plans.current,
    queryFn: async () => {
      const data = await api.get<{
        usage: CoCompanyPlanUsage;
        resources?: ResourceUsage[];
        plan?: { slug?: string; name?: string; value?: number | string | null };
        anniversaryDate?: string | null;
        renewsAt?: string | null;
        daysRemaining?: number | null;
      }>('/plans/current');
      if (!data?.usage) return null;
      return {
        ...data.usage,
        resources: data.resources ?? [],
        planSlug: data.plan?.slug ?? null,
        planName: data.plan?.name ?? null,
        planValue: data.plan?.value == null ? null : Number(data.plan.value),
        anniversaryDate: data.anniversaryDate ?? null,
        renewsAt: data.renewsAt ?? null,
        daysRemaining: data.daysRemaining ?? null,
      };
    },
    enabled: !!companyId,
    retry: false,
  });
}

export interface QuotaState {
  used: number;
  limit: number;
  remaining: number;
  percent: number;
  reached: boolean;
  warning: boolean;
  planName: string;
  isLoading: boolean;
}

// Maps the v2-local `QuotaResource` enum onto the normalized
// `(app, resource)` keys served by `/plans/current.resources[]`.
// Returning `null` means "this resource still lives on the wide `usage`
// columns" — the caller falls back to `data[key]` lookup.
function legacyToResource(
  key: QuotaResource,
): { app: string; resource: string } | null {
  switch (key) {
    case 'maxBrands':
    case 'maxUsers':
    case 'maxRoles':
    case 'maxSurveys':
      return null; // shared cap, still on usage table
    case 'projects':
    case 'xotskiEventGroups':
      return { app: 'xotski', resource: 'event_group' };
    case 'groups':
      return { app: 'xotski', resource: 'event_group' };
    case 'events':
    case 'xotskiEvents':
      return { app: 'xotski', resource: 'event' };
    case 'xotskiStations':
      return { app: 'xotski', resource: 'station' };
    case 'xotskiStocks':
      return { app: 'xotski', resource: 'stock' };
  }
}

export function usePlanQuota(resource: QuotaResource): QuotaState {
  const { data, isLoading } = useCurrentPlan();

  if (!data) {
    return {
      used: 0,
      limit: 0,
      remaining: 0,
      percent: 0,
      reached: false,
      warning: false,
      planName: isLoading ? 'Loading…' : '—',
      isLoading,
    };
  }

  // Prefer the normalized resources[] when the key maps to it; otherwise
  // fall back to the legacy wide-column lookup so shared caps (maxBrands)
  // keep working until the deprecation window closes.
  const mapped = legacyToResource(resource);
  let used = 0;
  let limit = 0;
  if (mapped) {
    const row = data.resources?.find(
      (r) => r.app === mapped.app && r.resource === mapped.resource,
    );
    used = row?.used ?? 0;
    limit = row?.limit ?? 0;
  } else {
    limit = (data[resource] as number | undefined) ?? 0;
  }

  const remaining = Math.max(0, limit - used);
  const percent = limit === 0 ? 0 : Math.round((used / limit) * 100);

  return {
    used,
    limit,
    remaining,
    percent,
    reached: limit > 0 && used >= limit,
    warning: limit > 0 && percent >= 80,
    planName: '—',
    isLoading,
  };
}
